import Button from '@asphalt-react/button';
import Flag from '@asphalt-react/flag';
import { Search } from '@asphalt-react/textfield';
import cn from 'classnames';
import firebase from 'firebase/app';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Image, Shimmer } from 'react-shimmer';
import Permission from '../../authorization/components/Permission';
import actions from '../../authorization/models/actions';
import ConfigProvider from '../../common/components/ConfigProvider';
import configKeys from '../../common/models/config-keys';
import routes from '../../common/models/route-constants';
import NoSearchResultsFound from '../../common/components/NoSearchResultsFound';
import styles from './IconHomePage.module.css';

function IconHomePage() {
    let { path } = useRouteMatch();
    let history = useHistory();
    const location = useLocation();
    const [icons, setIcons] = useState([]);
    const [notification, setNotification] = useState(null);
    const [filteredIcons, setFilteredIcons] = useState([]);
    const [filterString, setFilterString] = useState('');

    const clearHistoryNotificationState = (history) => {
        let state = { ...history.location.state };
        delete state.notification;
        history.replace({
            ...history.location,
            state,
        });
    };

    const filter = _.debounce((term) => {
        setFilterString(term);
        filterList(term);
    }, 300);

    const filterList = (inputFilterString) => {
        if (_.isEmpty(inputFilterString)) {
            setFilteredIcons(icons);
            return;
        }
        // constructing the regex string for case-insensitive matching
        var regex = new RegExp(_.escapeRegExp(inputFilterString), 'i');

        setFilteredIcons(
            _.filter(icons, (icon) => {
                return icon.data().combined_name.match(regex);
            })
        );
    };

    useEffect(() => {
        if (location.state && location['state'].notification) {
            setNotification(location.state.notification);
        }

        // set listener
        var db = firebase.firestore();
        const unsubscribeGetAllIconsListener = db
            .collection('icons')
            .orderBy('created_at', 'desc')
            .onSnapshot(
                function (snapshot) {
                    setIcons(snapshot.docs);
                    if (_.isEmpty(filterString)) {
                        setFilteredIcons(snapshot.docs);
                    }
                },
                function (error) {
                    throw error;
                }
            );
        return () => {
            unsubscribeGetAllIconsListener();
        };
    }, [history, location, filterString]);

    function getIconImageView(doc) {
        if ('loading' in HTMLImageElement.prototype) {
            // Browser supports `loading` atttribute
            // Use img tag with loading attribute set as lazy to support lazy loading
            // TODO: Use Image component here along with loading attribute
            return (
                <img
                    loading='lazy'
                    src={doc.data().img_url}
                    className={styles.image}
                    alt={doc.data().combined_name}
                />
            );
        } else {
            // Browser doesn't support `loading` attribute
            // Use Image component from react-shimmer to show shimmer while image is loading
            return (
                <Image
                    src={doc.data().img_url}
                    className={styles.image}
                    fallback={<Shimmer width={360} height={240} />}
                    NativeImgProps={{ alt: doc.data().combined_name }}
                />
            );
        }
    }

    function getIconItem(doc) {
        return (
            <Link
                to={`${path}/${doc.id}`}
                key={doc.data().combined_name}
                className={styles.iconCard}>
                <p className={styles.iconName}>{doc.data().combined_name}</p>
                <div className={styles.imageContainer}>
                    {getIconImageView(doc)}
                </div>
            </Link>
        );
    }
    return (
        <div style={{ width: '100%' }}>
            <div className={cn('panel', { show: notification })}>
                <div className={cn('animateFlag', { visible: notification })}>
                    <Flag
                        success
                        floating
                        title={notification ? notification.title : ''}
                        content={notification ? notification.content : ''}
                        actionable
                        engageCaption={notification ? notification.ctaText : ''}
                        engageProps={{
                            onClick: () => {
                                if (notification) {
                                    setNotification(null);
                                    clearHistoryNotificationState(history);
                                    history.push(
                                        `${path}/${notification.iconId}`
                                    );
                                }
                            },
                        }}
                        onDismiss={() => {
                            setNotification(null);
                            clearHistoryNotificationState(history);
                        }}
                    />
                </div>
            </div>
            <div className={styles.homePageContainer}>
                <div className={cn(styles.topBarContainer, 'shadowLow')}>
                    <div className={styles.topBar}>
                        <div className={styles.headerContainer}>
                            <h3 className={styles.heading}>Icons</h3>
                            <div className={styles.buttonContainer}>
                                <ConfigProvider
                                    configKey={
                                        configKeys.IS_CREATE_ICON_ENABLED
                                    }>
                                    <Permission action={actions.CREATE_ICON}>
                                        <Button
                                            onClick={() => {
                                                history.push(
                                                    `${path}/${routes.CREATE}`
                                                );
                                            }}>
                                            Add icon
                                        </Button>
                                    </Permission>
                                </ConfigProvider>
                            </div>
                        </div>
                        <Search
                            onChange={(e) => {
                                filter(e.target.value);
                            }}
                            size='m'
                            placeholder='Search for icons by key or name'
                            stretch
                        />
                    </div>
                </div>
                <div className={styles.gridContainer}>
                    {filteredIcons.length > 0 ? (
                        <div className={styles.grid}>
                            {filteredIcons.map((doc) => getIconItem(doc))}
                        </div>
                    ) : !_.isEmpty(filterString) ? (
                        <NoSearchResultsFound searchQuery={filterString} />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default IconHomePage;
